import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { PlatformAuthProvider } from "./contexts/PlatformAuthContext";
import { usePlatformAuth } from "./hooks/usePlatformAuth";
import { LoginPage } from "./pages/Login";
import { AccessDeniedPage } from "./pages/AccessDenied";
import { HomePage } from "./pages/Home";
import { TenantsPage } from "./pages/Tenants";
import { TenantDetailLayout } from "./pages/TenantDetail";
import { TenantFeaturesPage } from "./pages/TenantFeatures";
import { TenantOverviewPage } from "./pages/TenantOverview";
import { TenantBrandingPage } from "./pages/TenantBranding";
import { TenantVocabularyPage } from "./pages/TenantVocabulary";
import { TenantActivityTypesPage } from "./pages/TenantActivityTypes";
import { TenantBenefitTypesPage } from "./pages/TenantBenefitTypes";
import { TenantAppPage } from "./pages/TenantApp";
import { TenantBillingPage } from "./pages/TenantBilling";
import { TenantLogPage } from "./pages/TenantLog";
import { BillingPage } from "./pages/Billing";
import { CreateTenantPage } from "./pages/CreateTenant";
import { PlaceholderPage } from "./pages/Placeholder";
import { colors } from "./lib/tokens";
import type { ReactNode } from "react";

function AuthGate({ children }: { children: ReactNode }) {
  const { loading, session, isPlatformAdmin } = usePlatformAuth();
  if (loading) {
    return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: colors.stone }}>Carregando...</div>;
  }
  if (!session) return <Navigate to="/login" replace />;
  if (!isPlatformAdmin) return <Navigate to="/access-denied" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <PlatformAuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/access-denied" element={<AccessDeniedPage />} />
          <Route path="/" element={<AuthGate><HomePage /></AuthGate>} />
          <Route path="/tenants" element={<AuthGate><TenantsPage /></AuthGate>} />
          <Route path="/tenants/new" element={<AuthGate><CreateTenantPage /></AuthGate>} />
          <Route path="/tenants/:slug" element={<AuthGate><TenantDetailLayout /></AuthGate>}>
            <Route index element={<TenantOverviewPage />} />
            <Route path="branding"       element={<TenantBrandingPage />} />
            <Route path="vocabulary"     element={<TenantVocabularyPage />} />
            <Route path="activity-types" element={<TenantActivityTypesPage />} />
            <Route path="benefit-types"  element={<TenantBenefitTypesPage />} />
            <Route path="app"            element={<TenantAppPage />} />
            <Route path="features"       element={<TenantFeaturesPage />} />
            <Route path="billing"        element={<TenantBillingPage />} />
            <Route path="log"            element={<TenantLogPage />} />
          </Route>
          <Route path="/billing" element={<AuthGate><BillingPage /></AuthGate>} />
          <Route path="/metrics" element={<AuthGate><PlaceholderPage title="Métricas" /></AuthGate>} />
          <Route path="/audit" element={<AuthGate><PlaceholderPage title="Audit log" /></AuthGate>} />
          <Route path="/broadcast" element={<AuthGate><PlaceholderPage title="Broadcast" /></AuthGate>} />
          <Route path="/team" element={<AuthGate><PlaceholderPage title="Equipe Klubora" /></AuthGate>} />
          <Route path="/settings" element={<AuthGate><PlaceholderPage title="Configurações" /></AuthGate>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </PlatformAuthProvider>
  );
}
